import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  RadioButtonUnchecked as UncheckedIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  createTheme,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tutorials } from "../config/tutorials";
import { usePageTutorial } from "../hooks/useTutorial";
import { AgentMessagingService } from "../services/AgentMessagingService";
import { CreateTodoData, Todo, todoService, UpdateTodoData } from "../services/TodoService";
import { getCurrentUser } from "../services/firebase";
import { DateUtils } from "../utils/dateUtils";
import { HelpIcon } from "./HelpIcon";
import { TimezoneUtils } from "../utils/timezoneUtils";

const priorities = [
  { value: "high", label: "High", color: "#d32f2f" },
  { value: "medium", label: "Medium", color: "#f57c00" },
  { value: "low", label: "Low", color: "#388e3c" },
];

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
  },
});

export const TodoListPage: React.FC = () => {
  const navigate = useNavigate();
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTodo, setNewTodo] = useState<CreateTodoData>({
    title: "",
    priority: "medium",
    deadline: "",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [filter, setFilter] = useState<"all" | "completed" | "pending">("pending");
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0 });
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const user = getCurrentUser();

  // Initialize tutorial for this page
  usePageTutorial("todo_page_tour", tutorials.todo);

  useEffect(() => {
    if (user) {
      loadTodos();
      loadStats();
    }
  }, [user, filter]);

  const loadTodos = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const todoList = await todoService.getTodos();
      // Filter todos based on current filter
      const filteredTodos = todoList.filter((todo) => {
        if (filter === "completed") return todo.completed;
        if (filter === "pending") return !todo.completed;
        return true; // "all" filter
      });
      setTodos(filteredTodos);
    } catch (err) {
      setError("Failed to load todos");
      console.error("Error loading todos:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!user) return;

    try {
      const todoStats = await todoService.getTodoStats();
      setStats(todoStats);
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const handleToggleComplete = async (todo: Todo) => {
    if (!user) return;

    setToggling(todo.id);

    // Optimistic update: immediately toggle the completion status in the UI
    setTodos((prevTodos) => prevTodos.map((t) => (t.id === todo.id ? { ...t, completed: !t.completed, updatedAt: new Date() } : t)));

    try {
      await todoService.markTodoCompleted(todo.id, !todo.completed);
      await loadTodos();
      await loadStats();
    } catch (err) {
      // Revert optimistic update on error
      setTodos((prevTodos) => prevTodos.map((t) => (t.id === todo.id ? { ...t, completed: todo.completed, updatedAt: todo.updatedAt } : t)));
      setError("Failed to update todo");
      console.error("Error updating todo:", err);
    } finally {
      setToggling(null);
    }
  };

  const handleDeleteTodo = async (todoId: string) => {
    if (!user) return;

    setDeleting(todoId);

    try {
      await todoService.deleteTodo(todoId);
      await loadTodos();
      await loadStats();
    } catch (err) {
      setError("Failed to delete todo");
      console.error("Error deleting todo:", err);
    } finally {
      setDeleting(null);
    }
  };

  const handleAIAssist = async (todo: Todo) => {
    try {
      setAiLoading(todo.id);

      // Find the PersonalAssistant agent
      const agentChatInfos = await AgentMessagingService.getAgentChatInfo();
      const personalAssistantAgent = agentChatInfos.find((a) => a.agentType === "PersonalAssistantAgent");

      if (!personalAssistantAgent) {
        throw new Error("PersonalAssistant agent not found");
      }

      const createdDate = DateUtils.formatForMessage(todo.createdAt);
      const deadlineDate = todo.deadline ? DateUtils.formatForMessage(todo.deadline) : null;
      const message = `Please help me with this todo: "${todo.title}"${todo.todoName ? ` (also known as: ${todo.todoName})` : ""}. Priority: ${todo.priority}${deadlineDate ? `, Deadline: ${deadlineDate}` : ""}. ${todo.completed ? "This todo is completed." : "This todo is pending."} Created: ${createdDate}.`;

      // Fire and forget API call
      AgentMessagingService.sendUserMessageToAgent(personalAssistantAgent.agentId, message).catch((error) => {
        console.error("Error sending AI assist message:", error);
      });

      // Navigate to chat with PersonalAssistant
      navigate(`/chat/${personalAssistantAgent.agentId}`);
    } catch (error) {
      console.error("Error initiating AI assist:", error);
      setError("Failed to start AI assistance");
    } finally {
      setAiLoading(null);
    }
  };

  const handleAddTodo = async () => {
    if (!user || !newTodo.title.trim()) return;

    setSaving(true);
    try {
      await todoService.createTodo(newTodo);
      setNewTodo({ title: "", priority: "medium", deadline: "" });
      setOpenDialog(false);
      await loadTodos();
      await loadStats();
    } catch (err) {
      setError("Failed to create todo");
      console.error("Error creating todo:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleEditTodo = (todo: Todo) => {
    setEditingTodo(todo);
    setNewTodo({
      title: todo.title,
      priority: todo.priority,
      deadline: todo.deadline ? TimezoneUtils.convertToString(todo.deadline).split("T")[0] : "",
    });
    setOpenDialog(true);
  };

  const handleUpdateTodo = async () => {
    if (!user || !editingTodo || !newTodo.title.trim()) return;

    setSaving(true);
    try {
      const updateData: UpdateTodoData = {
        title: newTodo.title,
        priority: newTodo.priority,
        deadline: newTodo.deadline || undefined,
      };

      await todoService.updateTodo(editingTodo.id, updateData);
      setNewTodo({ title: "", priority: "medium", deadline: "" });
      setEditingTodo(null);
      setOpenDialog(false);
      await loadTodos();
    } catch (err) {
      setError("Failed to update todo");
      console.error("Error updating todo:", err);
    } finally {
      setSaving(false);
    }
  };

  const filteredTodos = todos.filter((todo) => {
    if (filter === "all") return true;
    if (filter === "completed") return todo.completed;
    if (filter === "pending") return !todo.completed;
    return true;
  });

  const progressPercentage = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;

  const getPriorityColor = (priority: string) => {
    const priorityObj = priorities.find((p) => p.value === priority);
    return priorityObj ? priorityObj.color : "#666";
  };

  const formatDate = (date: any) => {
    return DateUtils.formatForDisplay(date);
  };

  const isOverdue = (deadline: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return deadline < today;
  };

  if (!user) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6">Please sign in to view your todos</Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AssignmentIcon sx={{ mr: 2, fontSize: 32, color: "primary.main" }} />
              <Typography variant="h4" component="h1">
                Todo List
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HelpIcon
                tutorialConfig={tutorials.todo}
                pageDescription="Manage your tasks and stay organized! Create todos, set priorities, add deadlines, and track your progress. Stay productive and focused on what matters most."
              />
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)} data-tutorial="add-todo">
                Add Task
              </Button>
            </Box>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress: {stats.completed} of {stats.total} tasks completed
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {Math.round(progressPercentage)}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressPercentage} sx={{ height: 8, borderRadius: 4 }} />
          </Box>

          {/* Filters */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip label="All" variant={filter === "all" ? "filled" : "outlined"} onClick={() => setFilter("all")} color={filter === "all" ? "primary" : "default"} />
            <Chip label="Pending" variant={filter === "pending" ? "filled" : "outlined"} onClick={() => setFilter("pending")} color={filter === "pending" ? "primary" : "default"} />
            <Chip label="Completed" variant={filter === "completed" ? "filled" : "outlined"} onClick={() => setFilter("completed")} color={filter === "completed" ? "primary" : "default"} />
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Todo List */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : filteredTodos.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  {filter === "all" ? "No todos yet. Add your first task!" : `No ${filter} todos.`}
                </Typography>
              </Box>
            ) : (
              <List data-tutorial="todo-list">
                {filteredTodos.map((todo) => (
                  <ListItem
                    key={todo.id}
                    sx={{
                      border: `1px solid ${todo.completed ? "#e0e0e0" : getPriorityColor(todo.priority)}`,
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: todo.completed ? "#f5f5f5" : "white",
                      opacity: todo.completed ? 0.7 : 1,
                    }}
                  >
                    <ListItemIcon>
                      {toggling === todo.id ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Checkbox checked={todo.completed} onChange={() => handleToggleComplete(todo)} icon={<UncheckedIcon />} checkedIcon={<CheckCircleIcon />} disabled={toggling === todo.id} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              textDecoration: todo.completed ? "line-through" : "none",
                              fontWeight: "bold",
                            }}
                          >
                            {todo.title}
                          </Typography>
                          <Chip
                            label={priorities.find((p) => p.value === todo.priority)?.label}
                            size="small"
                            sx={{
                              bgcolor: getPriorityColor(todo.priority),
                              color: "white",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                            {todo.deadline && (
                              <Box sx={{ display: "flex", alignItems: "center" }}>
                                <CalendarIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: !todo.completed && isOverdue(todo.deadline) ? "#d32f2f" : "text.secondary",
                                  }}
                                >
                                  Due: {formatDate(todo.deadline)}
                                  {!todo.completed && isOverdue(todo.deadline) && " (Overdue)"}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton size="small" onClick={() => handleAIAssist(todo)} disabled={aiLoading === todo.id} sx={{ color: "primary.main" }} title="Action this todo with AI">
                          {aiLoading === todo.id ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                        </IconButton>
                        <IconButton size="small" onClick={() => handleEditTodo(todo)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteTodo(todo.id)} disabled={deleting === todo.id}>
                          {deleting === todo.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Todo Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => {
            setOpenDialog(false);
            setEditingTodo(null);
            setNewTodo({ title: "", priority: "medium", deadline: "" });
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{editingTodo ? "Edit Task" : "Add New Task"}</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 2,
                mt: 1,
              }}
            >
              <Box sx={{ gridColumn: "1 / -1" }}>
                <TextField fullWidth label="Task Title" value={newTodo.title} onChange={(e) => setNewTodo({ ...newTodo, title: e.target.value })} variant="outlined" required />
              </Box>
              <Box>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select value={newTodo.priority} onChange={(e) => setNewTodo({ ...newTodo, priority: e.target.value as "high" | "medium" | "low" })} label="Priority">
                    {priorities.map((priority) => (
                      <MenuItem key={priority.value} value={priority.value}>
                        {priority.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <TextField
                  fullWidth
                  label="Deadline"
                  type="date"
                  value={newTodo.deadline}
                  onChange={(e) => setNewTodo({ ...newTodo, deadline: e.target.value })}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenDialog(false);
                setEditingTodo(null);
                setNewTodo({ title: "", priority: "medium", deadline: "" });
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={editingTodo ? handleUpdateTodo : handleAddTodo}
              disabled={!newTodo.title.trim() || saving}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              {saving ? "Saving..." : editingTodo ? "Update Task" : "Add Task"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};
